<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { StatisticsTableDataItem } from '@/components/dashboard/dashboard.interfaces.ts'
import { calculateConversion } from '@/utils/util.ts'
import { STATS_TABLE_ITEMS_PER_PAGE } from '@/config/visual.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
const { t } = useTranslations()

const props = defineProps<{
  data: StatisticsTableDataItem[]
}>()

const currentPage = ref(1)

const reversedData = computed(() => {
  return props.data.slice().reverse()
})

const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * STATS_TABLE_ITEMS_PER_PAGE
  const end = start + STATS_TABLE_ITEMS_PER_PAGE
  return reversedData.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(reversedData.value.length / STATS_TABLE_ITEMS_PER_PAGE)
})

function nextPage() {

  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

function prevPage() {

  if (currentPage.value > 1) {
    currentPage.value--
  }
}

watch(() => props.data, () => {
  currentPage.value = 1
})
</script>

<template>
  <div class="w-full">
    <table class="min-w-full table-auto border-collapse border border-gray-200">
      <thead>
      <tr class="bg-gray-100">
        <th class="px-4 py-2 text-center border-b text-sm">{{ t('analytics.date') }}</th>
        <th class="px-4 py-2 text-center border-b border-l text-sm">{{ t('analytics.numberOfChats') }}</th>
        <th class="px-4 py-2 text-center border-b border-l text-sm">{{ t('analytics.numberOfContactsCollected') }}</th>
        <th class="px-4 py-2 text-center border-b border-l text-sm">{{ t('analytics.conversionRate') }} (%)</th>
      </tr>
      </thead>

      <tbody>
      <tr v-for="(row, index) in paginatedData" :key="index" class="hover:bg-gray-200">
        <td class="px-4 py-2 text-center border-b font-thin text-sm w-40">{{ row.formattedDate }}</td>
        <td class="px-4 py-2 text-center border-b border-l font-extralight text-sm">{{ row.chatsAmount }}</td>
        <td class="px-4 py-2 text-center border-b border-l font-extralight text-sm">{{ row.contactsAmount }}</td>
        <td class="px-4 py-2 text-center border-b border-l font-normal text-sm">
          {{ calculateConversion(row.chatsAmount, row.contactsAmount) }}
        </td>
      </tr>
      </tbody>
    </table>

    <div v-if="totalPages > 1" class="flex justify-center items-center gap-4 mt-4">
      <button
        @click="prevPage"
        :disabled="currentPage === 1"
        class="px-2 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
      >
        {{ t('common.pagePagination.previous') }}
      </button>
      <span class="text-sm text-gray-600">
        {{ t('common.pagePagination.page') }} {{ currentPage }}
        {{ t('common.pagePagination.of') }} {{ totalPages }}
      </span>
      <button
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="px-4 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
      >
        {{ t('common.pagePagination.next') }}
      </button>
    </div>
  </div>
</template>
